import React, { useState, useRef, useEffect } from 'react';
import html2canvas from 'html2canvas';
import { collection, query, orderBy, limit, onSnapshot, doc, addDoc, serverTimestamp, updateDoc, deleteDoc } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';
import { db, storage } from '../firebase/config';
import ThreadService from '../services/ThreadService';

const SlideGenerator = ({ user }) => {
  const [tweetUrl, setTweetUrl] = useState('');
  const [slides, setSlides] = useState([]);
  const [slideImages, setSlideImages] = useState([]);
  const [isGenerating, setIsGenerating] = useState(false);
  const [error, setError] = useState('');
  const [currentSlide, setCurrentSlide] = useState(0);
  const [loadingProgress, setLoadingProgress] = useState(0);
  const [loadingMessage, setLoadingMessage] = useState('');
  const [isDownloading, setIsDownloading] = useState(false);
  const [recentSlides, setRecentSlides] = useState([]);
  const [isLoadingRecent, setIsLoadingRecent] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isGeneratingPreviews, setIsGeneratingPreviews] = useState(false);
  const slideRenderRef = useRef(null);
  const threadService = new ThreadService();

  // Load recent slides from Firebase
  useEffect(() => {
    if (!user) return;

    setIsLoadingRecent(true);
    const slidesRef = collection(db, 'users', user.uid, 'slides');
    const q = query(slidesRef, orderBy('createdAt', 'desc'), limit(10));

    const unsubscribe = onSnapshot(q, (snapshot) => {
      const slidesData = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setRecentSlides(slidesData);
      setIsLoadingRecent(false);
    });

    return () => unsubscribe();
  }, [user]);

  // Cleanup slide images on component unmount
  useEffect(() => {
    return () => {
      // Cleanup any object URLs created for slide images
      slideImages.forEach(imageUrl => {
        if (typeof imageUrl === 'string' && imageUrl.startsWith('blob:')) {
          URL.revokeObjectURL(imageUrl);
        }
      });
    };
  }, [slideImages]);

  // Save slides to Firebase with images
  const saveSlideSet = async (slidesData, originalTweetUrl) => {
    if (!user || !slidesData.length) return;

    setIsSaving(true);
    try {
      // Generate images for all slides
      const slideImages = [];
      for (let i = 0; i < slidesData.length; i++) {
        const imageData = await renderSlideAsImage(slidesData[i], i + 1, slidesData.length);
        if (imageData) {
          slideImages.push(imageData);
        }
      }

      // Upload images to Firebase Storage
      const uploadPromises = slideImages.map(async (imageData, index) => {
        const blob = dataURLToBlob(imageData);
        const fileName = `slide-${index + 1}-${Date.now()}.png`;
        const storageRef = ref(storage, `slides/${user.uid}/${fileName}`);
        const uploadResult = await uploadBytes(storageRef, blob);
        return getDownloadURL(uploadResult.ref);
      });

      const imageUrls = await Promise.all(uploadPromises);

      // Save to Firestore
      const slideSetData = {
        userId: user.uid,
        tweetUrl: originalTweetUrl,
        slides: slidesData,
        imageUrls: imageUrls,
        slideCount: slidesData.length,
        createdAt: serverTimestamp(),
        title: slidesData[0]?.title || 'Untitled Slide Set'
      };

      await addDoc(collection(db, 'users', user.uid, 'slides'), slideSetData);
    } catch (error) {
      console.error('Error saving slides:', error);
    } finally {
      setIsSaving(false);
    }
  };

  // Helper function to convert data URL to Blob
  const dataURLToBlob = (dataURL) => {
    const arr = dataURL.split(',');
    const mime = arr[0].match(/:(.*?);/)[1];
    const bstr = atob(arr[1]);
    let n = bstr.length;
    const u8arr = new Uint8Array(n);
    while (n--) {
      u8arr[n] = bstr.charCodeAt(n);
    }
    return new Blob([u8arr], { type: mime });
  };

  // Generate slide images for preview
  const generateSlideImages = async (slidesData) => {
    setIsGeneratingPreviews(true);
    try {
      const images = [];
      for (let i = 0; i < slidesData.length; i++) {
        const imageData = await renderSlideAsImage(slidesData[i], i + 1, slidesData.length);
        images.push(imageData);
      }
      setSlideImages(images);
    } catch (error) {
      console.error('Error generating slide images:', error);
    } finally {
      setIsGeneratingPreviews(false);
    }
  };

  // Generate slide images with progress updates during generation
  const generateSlideImagesWithProgress = async (slidesData) => {
    try {
      const images = [];
      const baseProgress = 87; // Starting progress
      const progressPerSlide = 8 / slidesData.length; // 8% total for image generation
      
      for (let i = 0; i < slidesData.length; i++) {
        setLoadingMessage(`🎨 Creating slide ${i + 1} of ${slidesData.length}...`);
        
        // Update progress more frequently during image generation
        const startProgress = baseProgress + (progressPerSlide * i);
        setLoadingProgress(Math.round(startProgress));
        
        // Simulate sub-steps within each slide generation
        await new Promise(resolve => setTimeout(resolve, 200));
        setLoadingProgress(Math.round(startProgress + (progressPerSlide * 0.3)));
        
        await new Promise(resolve => setTimeout(resolve, 300));
        setLoadingProgress(Math.round(startProgress + (progressPerSlide * 0.6)));
        
        const imageData = await renderSlideAsImage(slidesData[i], i + 1, slidesData.length);
        images.push(imageData);
        
        // Final progress update for this slide
        setLoadingProgress(Math.round(startProgress + progressPerSlide));
        
        // Small delay to show progress
        await new Promise(resolve => setTimeout(resolve, 150));
      }
      
      setSlideImages(images);
    } catch (error) {
      console.error('Error generating slide images with progress:', error);
      // Fallback to regular generation
      await generateSlideImages(slidesData);
    }
  };

  // Load saved slides
  const loadSavedSlides = (slideSet) => {
    setSlides(slideSet.slides);
    setCurrentSlide(0);
    setTweetUrl(slideSet.tweetUrl);
    
    // Load existing images if available
    if (slideSet.imageUrls && slideSet.imageUrls.length > 0) {
      setSlideImages(slideSet.imageUrls);
    } else {
      // Generate new images for preview
      generateSlideImages(slideSet.slides);
    }
  };

  // Delete saved slide set
  const deleteSavedSlides = async (slideSetId, imageUrls) => {
    if (!user) return;

    try {
      // Delete images from storage
      const deletePromises = imageUrls.map(async (url) => {
        try {
          const storageRef = ref(storage, url);
          await deleteObject(storageRef);
        } catch (error) {
          console.warn('Error deleting image:', error);
        }
      });

      await Promise.all(deletePromises);

      // Delete from Firestore
      await deleteDoc(doc(db, 'users', user.uid, 'slides', slideSetId));
    } catch (error) {
      console.error('Error deleting slides:', error);
    }
  };

  // Format time ago
  const formatTimeAgo = (timestamp) => {
    if (!timestamp) return 'Unknown';
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    const now = new Date();
    const seconds = Math.floor((now - date) / 1000);
    
    if (seconds < 60) return 'just now';
    const minutes = Math.floor(seconds / 60);
    if (minutes < 60) return `${minutes}m ago`;
    const hours = Math.floor(minutes / 60);
    if (hours < 24) return `${hours}h ago`;
    const days = Math.floor(hours / 24);
    if (days < 30) return `${days}d ago`;
    return date.toLocaleDateString();
  };

  const extractTweetId = (url) => {
    const regex = /twitter\.com\/\w+\/status\/(\d+)|x\.com\/\w+\/status\/(\d+)/;
    const match = url.match(regex);
    return match ? (match[1] || match[2]) : null;
  };

  // Generate AI background images for slides
  const generateAIBackgroundImages = async (slides, tweetData) => {
    try {
      // Generate Claude-powered image prompts for each slide
      const slidesWithPrompts = [];
      
      for (let i = 0; i < slides.length; i++) {
        const slide = slides[i];
        
        try {
          // Use Claude to generate intelligent image prompt
          setLoadingMessage(`🧠 Creating AI image prompt ${i + 1}/${slides.length}...`);
          
          const promptResponse = await fetch('/.netlify/functions/generate-image-prompts', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({
              slides: slides,
              tweetText: tweetData.text || '',
              slideIndex: i
            }),
          });

          let imagePrompt;
          if (promptResponse.ok) {
            const promptData = await promptResponse.json();
            imagePrompt = promptData.imagePrompt;
            console.log(`Claude generated prompt for slide ${i + 1}:`, imagePrompt);
          } else {
            // Fallback to basic prompt generation
            console.warn(`Failed to get Claude prompt for slide ${i + 1}, using fallback`);
            imagePrompt = generateBasicPrompt(slide, tweetData, i === 0);
          }

          slidesWithPrompts.push({
            ...slide,
            imagePrompt: imagePrompt
          });
        } catch (error) {
          console.error(`Error generating prompt for slide ${i + 1}:`, error);
          // Fallback to basic prompt generation
          const fallbackPrompt = generateBasicPrompt(slide, tweetData, i === 0);
          slidesWithPrompts.push({
            ...slide,
            imagePrompt: fallbackPrompt
          });
        }
      }

      // Generate images using Runware API with progress updates
      const slidesWithImages = [];
      const totalSlides = slidesWithPrompts.length;

      for (let i = 0; i < slidesWithPrompts.length; i++) {
        const slide = slidesWithPrompts[i];
        
        // Update progress for each image generation and analysis (80-87% total)
        const imageGenProgress = 80 + ((i / totalSlides) * 3.5); // 80-83.5% for image generation
        setLoadingMessage(`🎨 Generating background ${i + 1}/${totalSlides}...`);
        setLoadingProgress(Math.round(imageGenProgress));

        try {
          // Generate proper UUID v4
          const taskUUID = 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function(c) {
            const r = Math.random() * 16 | 0;
            const v = c === 'x' ? r : (r & 0x3 | 0x8);
            return v.toString(16);
          });
          
          const runwarePayload = {
            taskType: "imageInference",
            model: "runware:100@1", // Runware model
            positivePrompt: slide.imagePrompt,
            height: 1024,
            width: 1024,
            numberResults: 1,
            outputType: "URL",
            outputFormat: "JPG",
            CFGScale: 7,
            steps: 20,
            taskUUID: taskUUID
          };

          console.log(`Using Claude-generated prompt for slide ${i + 1}:`, slide.imagePrompt);

          const runwareResponse = await fetch('https://api.runware.ai/v1', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': 'Bearer sDI413Ze5vQF9XqjwpNnpK61Bu0BbDJS'
            },
            body: JSON.stringify([runwarePayload])
          });

          if (runwareResponse.ok) {
            const runwareData = await runwareResponse.json();
            
            if (runwareData.data && runwareData.data.length > 0 && runwareData.data[0].imageURL) {
              console.log(`Generated background image for slide ${i + 1}:`, runwareData.data[0].imageURL);
              
              // Analyze the image for optimal text styling
              const analysisProgress = 83.5 + ((i / totalSlides) * 3.5); // 83.5-87% for analysis
              setLoadingMessage(`🔍 Analyzing image ${i + 1}/${totalSlides} for text styling...`);
              setLoadingProgress(Math.round(analysisProgress));
              const textStyling = await analyzeImageForTextStyling(runwareData.data[0].imageURL);
              
              slidesWithImages.push({
                ...slide,
                backgroundImageUrl: runwareData.data[0].imageURL,
                imageUUID: runwareData.data[0].imageUUID,
                textStyling: textStyling
              });
            } else {
              // Fallback if no image URL
              console.warn(`No image URL in response for slide ${i + 1}:`, runwareData);
              slidesWithImages.push({
                ...slide,
                textStyling: getDefaultTextStyling()
              });
            }
          } else {
            // Log the error details
            const errorText = await runwareResponse.text();
            console.error(`Runware API error for slide ${i + 1}:`, {
              status: runwareResponse.status,
              statusText: runwareResponse.statusText,
              error: errorText,
              payload: runwarePayload
            });
            slidesWithImages.push({
              ...slide,
              textStyling: getDefaultTextStyling()
            });
          }
        } catch (error) {
          console.error(`Error generating image for slide ${i + 1}:`, error);
          slidesWithImages.push({
            ...slide,
            textStyling: getDefaultTextStyling()
          }); // Continue without background image
        }

        // Small delay between requests to avoid rate limiting
        await new Promise(resolve => setTimeout(resolve, 500));
      }

      return slidesWithImages;
    } catch (error) {
      console.error('Error in generateAIBackgroundImages:', error);
      return slides; // Return original slides if everything fails
    }
  };

  // Basic fallback prompt generation (simplified version of old logic)
  const generateBasicPrompt = (slide, tweetData, isFirstSlide) => {
    const content = (slide.title + ' ' + (slide.content || '') + ' ' + (tweetData.text || '')).toLowerCase();
    
    let basePrompt = '';
    
    if (content.includes('money') || content.includes('income') || content.includes('business')) {
      basePrompt = isFirstSlide ? 
        'successful entrepreneur, luxury lifestyle, money success, professional environment, cinematic lighting' :
        'modern office setup, business success, clean professional aesthetic';
    } else if (content.includes('fitness') || content.includes('gym') || content.includes('workout')) {
      basePrompt = isFirstSlide ?
        'athletic person in gym, fitness motivation, dramatic lighting, professional photography' :
        'gym equipment, fitness lifestyle, clean modern aesthetic';
    } else {
      basePrompt = isFirstSlide ?
        'modern lifestyle concept, professional photography, dramatic lighting, engaging visual' :
        'clean modern aesthetic, professional lifestyle, minimalist design';
    }
    
    return basePrompt + ', high quality, 4k photography, high contrast';
  };



  // Analyze background image using AI to determine optimal text styling
  const analyzeImageForTextStyling = async (imageUrl) => {
    try {
      setLoadingMessage('🔍 Analyzing image for optimal text styling...');
      
      // Use Gemini Vision API to analyze the image
      const analysisResponse = await fetch('/.netlify/functions/analyze-image-styling', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          imageUrl: imageUrl
        }),
      });

      let analysis = null;
      if (analysisResponse.ok) {
        const responseData = await analysisResponse.json();
        analysis = responseData.analysis || responseData;
      } else {
        console.warn('Image analysis failed, using local analysis');
        analysis = await getLocalImageAnalysisData(imageUrl);
      }

      // Choose Instagram Story style based on analysis
      const instagramStyle = chooseInstagramStoryStyle(imageUrl, analysis);
      
      return {
        instagramStyle: instagramStyle,
        analysis: analysis,
        // Keep basic compatibility
        color: instagramStyle.contentColor,
        textShadow: instagramStyle.contentShadow,
        border: 'none',
        background: 'none'
      };
    } catch (error) {
      console.error('Error analyzing image for text styling:', error);
      const fallbackAnalysis = await getLocalImageAnalysisData(imageUrl);
      const instagramStyle = chooseInstagramStoryStyle(imageUrl, fallbackAnalysis);
      
      return {
        instagramStyle: instagramStyle,
        analysis: fallbackAnalysis,
        color: instagramStyle.contentColor,
        textShadow: instagramStyle.contentShadow,
        border: 'none',
        background: 'none'
      };
    }
  };

  // Local image analysis fallback that works without API calls
  const getLocalImageAnalysisData = async (imageUrl) => {
    try {
      // Create an image element to analyze locally
      const img = new Image();
      img.crossOrigin = 'anonymous';
      
      return new Promise((resolve) => {
        img.onload = () => {
          try {
            // Create canvas to analyze image colors
            const canvas = document.createElement('canvas');
            const ctx = canvas.getContext('2d');
            
            // Sample the image at lower resolution for speed
            const sampleSize = 50;
            canvas.width = sampleSize;
            canvas.height = sampleSize;
            
            ctx.drawImage(img, 0, 0, sampleSize, sampleSize);
            const imageData = ctx.getImageData(0, 0, sampleSize, sampleSize);
            const data = imageData.data;
            
            // Calculate average brightness and dominant colors
            let totalBrightness = 0;
            let redSum = 0, greenSum = 0, blueSum = 0;
            const pixelCount = data.length / 4;
            
            for (let i = 0; i < data.length; i += 4) {
              const r = data[i];
              const g = data[i + 1];
              const b = data[i + 2];
              
              // Calculate brightness (perceived luminance)
              const brightness = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
              totalBrightness += brightness;
              
              redSum += r;
              greenSum += g;
              blueSum += b;
            }
            
            const avgBrightness = totalBrightness / pixelCount;
            const avgRed = Math.round(redSum / pixelCount);
            const avgGreen = Math.round(greenSum / pixelCount);
            const avgBlue = Math.round(blueSum / pixelCount);
            
            // Determine background type
            let backgroundType;
            if (avgBrightness > 0.7) {
              backgroundType = 'light';
            } else if (avgBrightness < 0.4) {
              backgroundType = 'dark';
            } else {
              backgroundType = 'medium';
            }
            
            // Return analysis data for Instagram story style selection
            const analysisData = {
              brightness: avgBrightness,
              backgroundType: backgroundType,
              dominantColors: [{
                r: avgRed,
                g: avgGreen,
                b: avgBlue
              }],
              averageBrightness: avgBrightness
            };
            
            console.log(`Local analysis: brightness=${avgBrightness.toFixed(2)}, backgroundType=${backgroundType}, dominantColor=rgb(${avgRed},${avgGreen},${avgBlue})`);
            resolve(analysisData);
            
          } catch (error) {
            console.error('Local image analysis error:', error);
            resolve({
              brightness: 0.5,
              backgroundType: 'medium',
              dominantColors: [{ r: 128, g: 128, b: 128 }],
              averageBrightness: 0.5
            });
          }
        };
        
        img.onerror = () => {
          console.warn('Failed to load image for local analysis');
          resolve({
            brightness: 0.5,
            backgroundType: 'medium',
            dominantColors: [{ r: 128, g: 128, b: 128 }],
            averageBrightness: 0.5
          });
        };
        
        // Set timeout for image loading
        setTimeout(() => {
          if (!img.complete) {
            console.warn('Image loading timeout');
            resolve({
              brightness: 0.5,
              backgroundType: 'medium',
              dominantColors: [{ r: 128, g: 128, b: 128 }],
              averageBrightness: 0.5
            });
          }
        }, 3000);
        
        img.src = imageUrl;
      });
      
    } catch (error) {
      console.error('Local analysis setup error:', error);
      return {
        brightness: 0.5,
        backgroundType: 'medium',
        dominantColors: [{ r: 128, g: 128, b: 128 }],
        averageBrightness: 0.5
      };
    }
  };

  // Enhanced default styling with variations
  const getEnhancedDefaultStyling = (imageUrl) => {
    // Create variation based on URL hash for consistency
    const hash = imageUrl ? imageUrl.split('').reduce((a, b) => {
      a = ((a << 5) - a) + b.charCodeAt(0);
      return a & a;
    }, 0) : 0;
    
    const stylingVariations = [
      {
        color: '#ffffff',
        textShadow: '2px 2px 4px rgba(0, 0, 0, 0.8), 0 0 6px rgba(0, 0, 0, 0.6)',
        border: 'none',
        background: 'none'
      },
      {
        color: '#ffffff',
        textShadow: '2px 2px 4px rgba(0, 0, 0, 0.9), 0 0 8px rgba(0, 0, 0, 0.7)',
        border: 'none',
        background: 'none'
      },
      {
        color: '#000000',
        textShadow: '1px 1px 2px rgba(255, 255, 255, 0.8), 0 0 4px rgba(255, 255, 255, 0.6)',
        border: 'none',
        background: 'none'
      }
    ];
    
    return stylingVariations[Math.abs(hash) % stylingVariations.length];
  };

  // Instagram Story Style Presets
  const getInstagramStoryPresets = () => {
    return [
      // Style 1: Modern Cyan/Teal
      {
        name: 'tech_glow',
        titleColor: '#ffffff',
        highlightColor: '#00ffff',
        contentColor: '#ffffff',
        titleShadow: '3px 3px 6px rgba(0, 0, 0, 0.9), 0 0 15px rgba(0, 255, 255, 0.4)',
        contentShadow: '2px 2px 4px rgba(0, 0, 0, 0.8), 0 0 8px rgba(0, 0, 0, 0.6)',
        titleFontWeight: '900',
        contentFontWeight: '600',
        titleTransform: 'uppercase',
        layoutStyle: 'center',
        brandingColor: '#00ffff'
      },
      // Style 2: Professional Pink
      {
        name: 'news_bold',
        titleColor: '#ffffff',
        highlightColor: '#ff1493',
        contentColor: '#ffffff',
        titleShadow: '3px 3px 6px rgba(0, 0, 0, 0.9), 0 0 12px rgba(255, 20, 147, 0.3)',
        contentShadow: '2px 2px 4px rgba(0, 0, 0, 0.8), 0 0 6px rgba(0, 0, 0, 0.6)',
        titleFontWeight: '900',
        contentFontWeight: '700',
        titleTransform: 'uppercase',
        layoutStyle: 'left',
        brandingColor: '#ff1493',
        brandingText: 'NEWS'
      },
      // Style 3: Elegant Gold
      {
        name: 'golden_luxury',
        titleColor: '#ffffff',
        highlightColor: '#ffd700',
        contentColor: '#ffffff',
        titleShadow: '3px 3px 6px rgba(0, 0, 0, 0.9), 0 0 12px rgba(255, 215, 0, 0.4)',
        contentShadow: '2px 2px 4px rgba(0, 0, 0, 0.8), 0 0 6px rgba(0, 0, 0, 0.6)',
        titleFontWeight: '900',
        contentFontWeight: '600',
        titleTransform: 'uppercase',
        layoutStyle: 'center',
        brandingColor: '#ffd700'
      },
      // Style 4: Vibrant Pink
      {
        name: 'hot_pink',
        titleColor: '#ffffff',
        highlightColor: '#ff0080',
        contentColor: '#ffffff',
        titleShadow: '3px 3px 6px rgba(0, 0, 0, 0.9), 0 0 12px rgba(255, 0, 128, 0.3)',
        contentShadow: '2px 2px 4px rgba(0, 0, 0, 0.8), 0 0 6px rgba(0, 0, 0, 0.6)',
        titleFontWeight: '900',
        contentFontWeight: '600',
        titleTransform: 'uppercase',
        layoutStyle: 'left',
        brandingColor: '#ff0080'
      },
      // Style 5: Clean Cyan
      {
        name: 'cyan_highlights',
        titleColor: '#ffffff',
        highlightColor: '#00ffff',
        contentColor: '#ffffff',
        titleShadow: '3px 3px 6px rgba(0, 0, 0, 0.9), 0 0 12px rgba(0, 255, 255, 0.3)',
        contentShadow: '2px 2px 4px rgba(0, 0, 0, 0.8), 0 0 6px rgba(0, 0, 0, 0.6)',
        titleFontWeight: '900',
        contentFontWeight: '600',
        titleTransform: 'uppercase',
        layoutStyle: 'left',
        brandingColor: '#00ffff'
      },
      // Style 6: Electric Blue
      {
        name: 'electric_blue',
        titleColor: '#ffffff',
        highlightColor: '#00d4ff',
        contentColor: '#ffffff',
        titleShadow: '3px 3px 6px rgba(0, 0, 0, 0.9), 0 0 12px rgba(0, 212, 255, 0.3)',
        contentShadow: '2px 2px 4px rgba(0, 0, 0, 0.8), 0 0 6px rgba(0, 0, 0, 0.6)',
        titleFontWeight: '900',
        contentFontWeight: '600',
        titleTransform: 'uppercase',
        layoutStyle: 'center',
        brandingColor: '#00d4ff'
      },
      // Style 7: Classic White
      {
        name: 'classic_white',
        titleColor: '#ffffff',
        contentColor: '#ffffff',
        titleShadow: '4px 4px 8px rgba(0, 0, 0, 0.9), 0 0 16px rgba(0, 0, 0, 0.7)',
        contentShadow: '3px 3px 6px rgba(0, 0, 0, 0.8), 0 0 10px rgba(0, 0, 0, 0.6)',
        titleFontWeight: '900',
        contentFontWeight: '700',
        titleTransform: 'uppercase',
        layoutStyle: 'center',
        brandingColor: '#ffffff'
      }
    ];
  };

  // Function to identify key words/phrases that should be highlighted
  const identifyKeyWordsForHighlight = (text) => {
    // Keywords that should be highlighted (names, numbers, companies, important words)
    const keywordPatterns = [
      // Numbers and dollar amounts
      /\$\d+[KMB]?/gi,
      /\d+[KMB]?\s*(MILLION|BILLION|THOUSAND)/gi,
      /\d+%/gi,
      
      // Names and companies
      /\b(MUSK|ELON|TESLA|SPACEX|TWITTER|X)\b/gi,
      /\b(AKON|WAKANDA|SENEGAL)\b/gi,
      /\b(APPLE|MICROSOFT|GOOGLE|AMAZON|META|FACEBOOK)\b/gi,
      /\b(GROK|CHATGPT|GPT|AI)\b/gi,
      /\b(BITCOIN|CRYPTO|ETH|ETHEREUM)\b/gi,
      
      // Action words and important phrases
      /\b(DROPPED|ANNOUNCED|REVEALED|LAUNCHED|RELEASED)\b/gi,
      /\b(SCRAPPED|CANCELLED|BANNED|REMOVED)\b/gi,
      /\b(BEATS|DESTROYS|OUTPERFORMS|DOMINATES)\b/gi,
      /\b(SECRET|EXCLUSIVE|LEAKED|BREAKING)\b/gi,
      /\b(NO EXCEPTIONS|OFFICIALLY|CONFIRMED)\b/gi,
      
      // Project and product names
      /\b[A-Z]{2,}\s+\d+\b/gi, // Like "GROK 4", "GPT 5"
      /\b\w+\s+(PROJECT|CITY|METHOD|SYSTEM)\b/gi
    ];
    
    const matches = [];
    keywordPatterns.forEach(pattern => {
      let match;
      while ((match = pattern.exec(text)) !== null) {
        matches.push({
          text: match[0],
          start: match.index,
          end: match.index + match[0].length
        });
      }
    });
    
    // Sort by start position and remove overlaps
    matches.sort((a, b) => a.start - b.start);
    const cleanMatches = [];
    matches.forEach(match => {
      const hasOverlap = cleanMatches.some(existing => 
        (match.start >= existing.start && match.start < existing.end) ||
        (match.end > existing.start && match.end <= existing.end)
      );
      if (!hasOverlap) {
        cleanMatches.push(match);
      }
    });
    
    return cleanMatches;
  };

  // Choose Instagram Story Style based on image analysis
  const chooseInstagramStoryStyle = (imageUrl, analysis) => {
    const presets = getInstagramStoryPresets();
    
    // Get image characteristics
    const brightness = analysis?.brightness || 0.5;
    const backgroundType = analysis?.backgroundType || 'medium';
    
    // Hash the image URL for consistent selection
    const hash = imageUrl ? imageUrl.split('').reduce((a, b) => {
      a = ((a << 5) - a) + b.charCodeAt(0);
      return a & a;
    }, 0) : 0;
    
    // Choose style based on image characteristics and hash
    let selectedStyle;
    
    if (brightness < 0.3) {
      // Very dark images - use bright, high-contrast styles
      const brightStyles = presets.filter(s => s.name === 'tech_glow' || s.name === 'golden_luxury' || s.name === 'hot_pink' || s.name === 'cyan_highlights');
      selectedStyle = brightStyles[Math.abs(hash) % brightStyles.length];
    } else if (brightness > 0.7) {
      // Very bright images - use high contrast styles
      const contrastStyles = presets.filter(s => s.name === 'news_bold' || s.name === 'hot_pink' || s.name === 'cyan_highlights');
      selectedStyle = contrastStyles[Math.abs(hash) % contrastStyles.length];
    } else {
      // Medium brightness - prefer news and tech styles
      const goodStyles = presets.filter(s => s.name === 'news_bold' || s.name === 'tech_glow' || s.name === 'hot_pink' || s.name === 'cyan_highlights');
      selectedStyle = goodStyles[Math.abs(hash) % goodStyles.length];
    }
    
    return selectedStyle;
  };

  // Get default text styling as fallback
  const getDefaultTextStyling = () => {
    return {
      color: '#ffffff',
      textShadow: '2px 2px 4px rgba(0, 0, 0, 0.8), 0 0 6px rgba(0, 0, 0, 0.6)',
      border: 'none',
      background: 'none',
      contrast: 'high'
    };
  };

  // Generate text styling based on image analysis
  const generateTextStyling = (analysis) => {
    const styling = {
      color: '#ffffff',
      textShadow: '2px 2px 4px rgba(0, 0, 0, 0.8)',
      border: 'none',
      background: 'none'
    };

    // Adjust based on dominant colors and brightness
    if (analysis.dominantColors) {
      const avgBrightness = analysis.averageBrightness || 0.5;
      
      if (avgBrightness > 0.7) {
        // Bright image - use dark text with light outline
        styling.color = '#1a1a1a';
        styling.textShadow = '0 0 8px rgba(255, 255, 255, 0.9), 2px 2px 4px rgba(255, 255, 255, 0.6)';
        styling.border = '2px solid rgba(255, 255, 255, 0.8)';
      } else if (avgBrightness < 0.3) {
        // Dark image - use white text with dark outline and glow
        styling.color = '#ffffff';
        styling.textShadow = '0 0 12px rgba(255, 255, 255, 0.8), 3px 3px 6px rgba(0, 0, 0, 0.9)';
        styling.border = '2px solid rgba(0, 0, 0, 0.6)';
      } else {
        // Medium brightness - adaptive styling
        const dominantColor = analysis.dominantColors[0];
        if (dominantColor) {
          // Use complementary color approach
          styling.color = '#ffffff';
          styling.textShadow = `0 0 10px rgba(0, 0, 0, 0.8), 2px 2px 4px rgba(${dominantColor.r}, ${dominantColor.g}, ${dominantColor.b}, 0.5)`;
          styling.background = `rgba(${255 - dominantColor.r}, ${255 - dominantColor.g}, ${255 - dominantColor.b}, 0.1)`;
        }
      }
    }

    // Add special effects based on image characteristics
    if (analysis.hasGradient) {
      styling.textShadow += ', 0 0 20px rgba(255, 255, 255, 0.3)';
    }

    if (analysis.contrast === 'low') {
      styling.background = 'rgba(0, 0, 0, 0.4)';
      styling.borderRadius = '8px';
      styling.padding = '4px 8px';
    }

    return styling;
  };

  const generateSlides = async () => {
    if (!tweetUrl.trim()) {
      setError('Please enter a tweet URL');
      return;
    }

    const tweetId = extractTweetId(tweetUrl);
    if (!tweetId) {
      setError('Please enter a valid Twitter/X URL');
      return;
    }

    setIsGenerating(true);
    setError('');
    setSlides([]);
    setSlideImages([]);
    setLoadingProgress(0);

    try {
      // Step 1: Check if this is a thread and fetch all tweets
      setLoadingMessage('📥 Getting your tweet...');
      setLoadingProgress(10);
      
      // Simulate initial connection delay
      await new Promise(resolve => setTimeout(resolve, 300));
      setLoadingProgress(15);
      
      // First, try to fetch as a single tweet
      const twitterResponse = await fetch('/api/twitter/tweet', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ tweetId }),
      });

      if (!twitterResponse.ok) {
        const errorText = await twitterResponse.text();
        throw new Error(`Failed to fetch tweet data: ${errorText}`);
      }

      const tweetData = await twitterResponse.json();
      setLoadingProgress(25);

      // Step 2: Check if this tweet has replies by the same author (thread detection)
      setLoadingMessage('🧵 Checking for thread content...');
      setLoadingProgress(30);
      
      let threadData = null;
      let isThread = false;
      
      try {
        // Use ThreadService to fetch complete thread
        const threadResult = await threadService.fetchCompleteThread(tweetId);
        
        if (threadResult.success && threadResult.tweets.length > 1) {
          // This is a thread with multiple tweets
          threadData = threadResult;
          isThread = true;
          console.log(`🧵 Found thread with ${threadResult.tweets.length} tweets`);
        } else {
          // Single tweet
          console.log('📝 Single tweet detected');
        }
      } catch (threadError) {
        console.log('Thread detection failed, treating as single tweet:', threadError);
        // Continue with single tweet processing
      }

      // Step 3: Analyzing content
      setLoadingMessage('🧠 Reading content...');
      setLoadingProgress(40);
      
      // Simulate analysis time
      await new Promise(resolve => setTimeout(resolve, 800));

      // Step 4: AI Generation
      setLoadingMessage('✨ Creating your slides...');
      setLoadingProgress(50);
      
      // Simulate AI processing time
      await new Promise(resolve => setTimeout(resolve, 500));
      setLoadingProgress(55);

      // Prepare content for slide generation
      let contentData;
      if (isThread && threadData) {
        // For threads, combine all tweet texts
        const threadText = threadData.tweets.map(tweet => tweet.text).join('\n\n');
        const firstTweet = threadData.tweets[0];
        
        contentData = {
          tweetText: threadText,
          tweetAuthor: firstTweet.author.username,
          tweetMetrics: {
            likes: threadData.threadStats.totalLikes,
            retweets: threadData.threadStats.totalRetweets,
            replies: threadData.threadStats.totalReplies
          },
          isThread: true,
          threadLength: threadData.tweets.length,
          originalTweetText: tweetData.text // Keep original for reference
        };
      } else {
        // Single tweet
        contentData = {
          tweetText: tweetData.text,
          tweetAuthor: tweetData.user.username,
          tweetMetrics: {
            likes: tweetData.metrics?.like_count || 0,
            retweets: tweetData.metrics?.retweet_count || 0,
            replies: tweetData.metrics?.reply_count || 0
          },
          isThread: false
        };
      }

      setLoadingProgress(60);
      const slidesResponse = await fetch('/.netlify/functions/generate-slides', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(contentData),
      });

      if (!slidesResponse.ok) {
        const errorText = await slidesResponse.text();
        throw new Error(`Failed to generate slides: ${errorText}`);
      }

      // Step 5: Processing results
      setLoadingMessage('📝 Processing slide content...');
      setLoadingProgress(75);
      
      const slidesData = await slidesResponse.json();
      
      // Step 6: Generate AI background images with Claude prompts
      setLoadingMessage('🤖 Creating intelligent image prompts...');
      setLoadingProgress(75);
      
      const slidesWithImages = await generateAIBackgroundImages(slidesData.slides, tweetData);
      
      // Step 7: Generating slide images
      setLoadingMessage('🎨 Creating slide images...');
      setLoadingProgress(87);
      
      setSlides(slidesWithImages);
      setCurrentSlide(0);
      
      // Generate preview images with progress updates
      await generateSlideImagesWithProgress(slidesWithImages);
      
      // Step 8: Saving to database
      setLoadingMessage('💾 Saving slides...');
      setLoadingProgress(95);
      
      // Automatically save slides to Firebase
      await saveSlideSet(slidesWithImages, tweetUrl);
      
      // Step 9: Complete
      setLoadingMessage('🎉 Your slides are ready!');
      setLoadingProgress(100);
      
      // Small delay to show completion
      await new Promise(resolve => setTimeout(resolve, 500));
    } catch (err) {
      console.error('Slide generation error:', err);
      
      // Provide simpler error messages
      let errorMessage = 'Could not make slides';
      
      if (err.message.includes('AI service is temporarily busy')) {
        errorMessage = '🤖 AI is busy. Please wait 30 seconds and try again.';
      } else if (err.message.includes('503') || err.message.includes('overloaded')) {
        errorMessage = '⏰ Server is busy. Please try again in a moment.';
      } else if (err.message.includes('rate limit')) {
        errorMessage = '⏱️ Too many tries. Please wait a minute and try again.';
      } else if (err.message.includes('Failed to fetch')) {
        errorMessage = '🌐 Connection problem. Please check your internet and try again.';
      } else {
        errorMessage = err.message || 'Could not make slides';
      }
      
      setError(errorMessage);
    } finally {
      setIsGenerating(false);
      setLoadingProgress(0);
      setLoadingMessage('');
    }
  };

  const renderSlideAsImage = async (slide, slideNumber, totalSlides) => {
    return new Promise((resolve) => {
      // Create a temporary container for the slide
      const slideContainer = document.createElement('div');
      slideContainer.style.position = 'absolute';
      slideContainer.style.left = '-9999px';
      slideContainer.style.top = '-9999px';
      slideContainer.style.width = '1024px';
      slideContainer.style.height = '1024px';
      
      // Use AI-generated background image if available, otherwise fallback to gradient
      if (slide.backgroundImageUrl) {
        slideContainer.style.background = `url(${slide.backgroundImageUrl})`;
        slideContainer.style.backgroundSize = 'cover';
        slideContainer.style.backgroundPosition = 'center';
        slideContainer.style.backgroundRepeat = 'no-repeat';
        
        // Add a semi-transparent overlay to ensure text readability
        const overlay = document.createElement('div');
        overlay.style.position = 'absolute';
        overlay.style.top = '0';
        overlay.style.left = '0';
        overlay.style.width = '100%';
        overlay.style.height = '100%';
        overlay.style.backgroundColor = 'rgba(0, 0, 0, 0.3)'; // Lighter overlay for better image visibility
        overlay.style.zIndex = '1';
        slideContainer.appendChild(overlay);
        
        // Add bottom vignette effect for Instagram story look
        const vignette = document.createElement('div');
        vignette.style.position = 'absolute';
        vignette.style.bottom = '-5px'; // Extend slightly below to eliminate gap
        vignette.style.left = '0';
        vignette.style.width = '100%';
        vignette.style.height = '42%'; // Slightly taller to ensure coverage
        vignette.style.background = 'linear-gradient(to top, rgba(0, 0, 0, 0.9) 0%, rgba(0, 0, 0, 0.6) 30%, rgba(0, 0, 0, 0.3) 60%, transparent 100%)';
        vignette.style.zIndex = '2';
        slideContainer.appendChild(vignette);
        
        // Ensure content is above all overlays
        slideContainer.style.position = 'relative';
        slideContainer.style.zIndex = '3';
      } else {
        // Fallback to gradient if no background image
        slideContainer.style.background = 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)';
        
        // Add vignette for gradient backgrounds too
        const vignette = document.createElement('div');
        vignette.style.position = 'absolute';
        vignette.style.bottom = '-5px'; // Extend slightly below to eliminate gap
        vignette.style.left = '0';
        vignette.style.width = '100%';
        vignette.style.height = '27%'; // Slightly taller to ensure coverage
        vignette.style.background = 'linear-gradient(to top, rgba(0, 0, 0, 0.6) 0%, transparent 100%)';
        vignette.style.zIndex = '2';
        slideContainer.appendChild(vignette);
      }
      
      // Container layout styling
      slideContainer.style.display = 'flex';
      slideContainer.style.flexDirection = 'column';
      slideContainer.style.justifyContent = 'center';
      slideContainer.style.alignItems = 'flex-start';
      slideContainer.style.padding = '60px';
      slideContainer.style.boxSizing = 'border-box';
      slideContainer.style.fontFamily = '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif';
      slideContainer.style.color = '#ffffff';
      slideContainer.style.textAlign = 'left';

      // Get Instagram Story styling from AI analysis or use default
      const textStyling = slide.textStyling || getDefaultTextStyling();
      const instagramStyle = textStyling.instagramStyle || chooseInstagramStoryStyle(slide.backgroundImageUrl, null);

      // Add slide number indicator with Instagram story style
      const slideIndicator = document.createElement('div');
      slideIndicator.style.position = 'absolute';
      slideIndicator.style.top = '30px';
      slideIndicator.style.right = '30px';
      slideIndicator.style.background = 'rgba(0, 0, 0, 0.4)';
      slideIndicator.style.backdropFilter = 'blur(10px)';
      slideIndicator.style.padding = '8px 16px';
      slideIndicator.style.borderRadius = '25px';
      slideIndicator.style.fontSize = '14px';
      slideIndicator.style.fontWeight = '600';
      slideIndicator.style.color = '#ffffff';
      slideIndicator.style.border = '1px solid rgba(255, 255, 255, 0.2)';
      slideIndicator.style.zIndex = '4'; // Ensure it's above everything
      slideIndicator.textContent = `${slideNumber}/${totalSlides}`;
      slideContainer.appendChild(slideIndicator);

      // Add branding element if style has branding text
      if (instagramStyle.brandingText) {
        const brandingElement = document.createElement('div');
        brandingElement.style.position = 'absolute';
        brandingElement.style.top = '30px';
        brandingElement.style.left = '30px';
        brandingElement.style.background = instagramStyle.brandingColor || '#ff1493';
        brandingElement.style.color = '#ffffff';
        brandingElement.style.padding = '6px 14px';
        brandingElement.style.borderRadius = '20px';
        brandingElement.style.fontSize = '12px';
        brandingElement.style.fontWeight = '900';
        brandingElement.style.letterSpacing = '0.5px';
        brandingElement.style.textTransform = 'uppercase';
        brandingElement.style.zIndex = '4';
        brandingElement.style.boxShadow = '0 2px 8px rgba(0, 0, 0, 0.3)';
        brandingElement.textContent = instagramStyle.brandingText;
        slideContainer.appendChild(brandingElement);
      }

      // For first slide, create one bottom-positioned main hook
      if (slideNumber === 1) {
        // Position text at bottom for first slide for maximum impact
        slideContainer.style.textAlign = 'center';
        slideContainer.style.alignItems = 'flex-end';
        slideContainer.style.justifyContent = 'flex-end';
        slideContainer.style.paddingBottom = '80px'; // Lower text positioning
        
        const mainTextElement = document.createElement('div');
        mainTextElement.style.fontSize = '60px';
        mainTextElement.style.fontWeight = instagramStyle.titleFontWeight || '900';
        mainTextElement.style.lineHeight = '1.1';
        mainTextElement.style.maxWidth = '90%';
        mainTextElement.style.wordWrap = 'break-word';
        mainTextElement.style.position = 'relative';
        mainTextElement.style.zIndex = '4'; // Ensure it's above everything
        mainTextElement.style.letterSpacing = '0.02em';
        mainTextElement.style.textAlign = 'center';
        
        // Apply Instagram Story styling
        mainTextElement.style.color = instagramStyle.titleColor;
        mainTextElement.style.textShadow = instagramStyle.titleShadow;
        if (instagramStyle.titleTransform) {
          mainTextElement.style.textTransform = instagramStyle.titleTransform;
        }
        
        // Use only the title for first slide with multi-colored text
        const titleText = slide.title;
        const keyWords = identifyKeyWordsForHighlight(titleText);
        
        if (keyWords.length > 0) {
          // Create multi-colored text
          let lastIndex = 0;
          keyWords.forEach((keyword, index) => {
            // Add regular text before keyword
            if (keyword.start > lastIndex) {
              const regularTextSpan = document.createElement('span');
              regularTextSpan.textContent = titleText.substring(lastIndex, keyword.start);
              regularTextSpan.style.color = instagramStyle.titleColor;
              regularTextSpan.style.textShadow = instagramStyle.titleShadow;
              mainTextElement.appendChild(regularTextSpan);
            }
            
            // Add highlighted keyword
            const keywordSpan = document.createElement('span');
            keywordSpan.textContent = keyword.text;
            keywordSpan.style.color = instagramStyle.highlightColor || instagramStyle.titleColor;
            keywordSpan.style.textShadow = instagramStyle.titleShadow;
            mainTextElement.appendChild(keywordSpan);
            
            lastIndex = keyword.end;
            
            // Add remaining text after last keyword
            if (index === keyWords.length - 1 && lastIndex < titleText.length) {
              const remainingTextSpan = document.createElement('span');
              remainingTextSpan.textContent = titleText.substring(lastIndex);
              remainingTextSpan.style.color = instagramStyle.titleColor;
              remainingTextSpan.style.textShadow = instagramStyle.titleShadow;
              mainTextElement.appendChild(remainingTextSpan);
            }
          });
        } else {
          // Fallback to single color if no keywords found
          mainTextElement.textContent = titleText;
          mainTextElement.style.color = instagramStyle.titleColor;
          mainTextElement.style.textShadow = instagramStyle.titleShadow;
        }
        
        slideContainer.appendChild(mainTextElement);

        // Add "SWIPE FOR MORE" text at the bottom for first slide
        const swipeText = document.createElement('div');
        swipeText.style.position = 'absolute';
        swipeText.style.bottom = '20px';
        swipeText.style.left = '50%';
        swipeText.style.transform = 'translateX(-50%)';
        swipeText.style.fontSize = '16px';
        swipeText.style.fontWeight = '600';
        swipeText.style.color = '#ffffff';
        swipeText.style.textTransform = 'uppercase';
        swipeText.style.letterSpacing = '1px';
        swipeText.style.zIndex = '4';
        swipeText.style.textShadow = '2px 2px 4px rgba(0, 0, 0, 0.8)';
        swipeText.textContent = 'SWIPE FOR MORE';
        slideContainer.appendChild(swipeText);
      } else {
        // Regular slides (2-5) keep separate title and content
        // Adjust layout based on Instagram story style
        if (instagramStyle.layoutStyle === 'center') {
          slideContainer.style.textAlign = 'center';
          slideContainer.style.alignItems = 'center';
        } else {
          slideContainer.style.textAlign = 'left';
          slideContainer.style.alignItems = 'flex-start';
        }
        
        const titleElement = document.createElement('h1');
        titleElement.style.fontSize = '56px';
        titleElement.style.fontWeight = instagramStyle.titleFontWeight || '900';
        titleElement.style.marginBottom = '32px';
        titleElement.style.lineHeight = '1.1';
        titleElement.style.position = 'relative';
        titleElement.style.zIndex = '4'; // Ensure it's above everything
        titleElement.style.letterSpacing = '0.01em';
        titleElement.style.wordSpacing = '0.05em';
        
        // Apply Instagram Story styling to title
        titleElement.style.color = instagramStyle.titleColor;
        titleElement.style.textShadow = instagramStyle.titleShadow;
        if (instagramStyle.titleTransform) {
          titleElement.style.textTransform = instagramStyle.titleTransform;
        }
        
        titleElement.textContent = slide.title;
        slideContainer.appendChild(titleElement);
        
        // Add content with proper line breaks and word wrapping
        const contentElement = document.createElement('div');
        contentElement.style.fontSize = '36px';
        contentElement.style.fontWeight = instagramStyle.contentFontWeight || '600';
        contentElement.style.lineHeight = '1.3';
        contentElement.style.maxWidth = '100%';
        contentElement.style.marginBottom = '32px';
        contentElement.style.wordWrap = 'break-word';
        contentElement.style.whiteSpace = 'pre-wrap';
        contentElement.style.position = 'relative';
        contentElement.style.zIndex = '4'; // Ensure it's above everything
        contentElement.style.letterSpacing = '0.01em';
        contentElement.style.wordSpacing = '0.05em';
        
        // Apply Instagram Story styling to content
        contentElement.style.color = instagramStyle.contentColor;
        contentElement.style.textShadow = instagramStyle.contentShadow;
        
        // Process content with proper line breaks and word spacing
        const contentLines = slide.content.split('\n');
        contentLines.forEach((line, index) => {
          const lineElement = document.createElement('div');
          lineElement.style.marginBottom = index < contentLines.length - 1 ? '12px' : '0';
          lineElement.style.wordSpacing = '0.1em';
          lineElement.style.lineHeight = 'inherit';
          
          // Add proper spacing between words
          const words = line.split(' ');
          words.forEach((word, wordIndex) => {
            const wordSpan = document.createElement('span');
            wordSpan.textContent = word;
            lineElement.appendChild(wordSpan);
            
            // Add space between words (except for the last word)
            if (wordIndex < words.length - 1) {
              const space = document.createTextNode(' ');
              lineElement.appendChild(space);
            }
          });
          
          contentElement.appendChild(lineElement);
        });
        slideContainer.appendChild(contentElement);
             }

      // Add callout if present with Instagram story styling
      if (slide.callout) {
        const calloutElement = document.createElement('div');
        
        // Use a more subtle, consistent background instead of gradient
        const brandColor = instagramStyle.brandingColor || '#ff1493';
        const rgbaColor = brandColor === '#ff1493' ? 'rgba(255, 20, 147, 0.15)' : 
                         brandColor === '#00ffff' ? 'rgba(0, 255, 255, 0.15)' :
                         brandColor === '#ffd700' ? 'rgba(255, 215, 0, 0.15)' :
                         brandColor === '#ff0080' ? 'rgba(255, 0, 128, 0.15)' :
                         brandColor === '#00d4ff' ? 'rgba(0, 212, 255, 0.15)' :
                         'rgba(255, 255, 255, 0.15)';
        
        calloutElement.style.background = rgbaColor;
        calloutElement.style.backdropFilter = 'blur(15px)';
        calloutElement.style.padding = '20px 30px';
        calloutElement.style.borderRadius = '25px';
        calloutElement.style.fontSize = '28px';
        calloutElement.style.fontWeight = instagramStyle.contentFontWeight || '700';
        calloutElement.style.border = `2px solid ${brandColor}40`;
        calloutElement.style.textAlign = instagramStyle.layoutStyle === 'center' ? 'center' : 'left';
        calloutElement.style.alignSelf = instagramStyle.layoutStyle === 'center' ? 'center' : 'flex-start';
        calloutElement.style.maxWidth = '90%';
        calloutElement.style.position = 'relative';
        calloutElement.style.zIndex = '4'; // Ensure it's above everything
        calloutElement.style.marginTop = '20px';
        calloutElement.style.boxShadow = `0 8px 32px rgba(0, 0, 0, 0.3), 0 4px 16px ${brandColor}20`;
        calloutElement.style.letterSpacing = '0.02em';
        calloutElement.style.lineHeight = '1.3';
        
        // Apply Instagram story styling for callout
        calloutElement.style.color = instagramStyle.contentColor;
        calloutElement.style.textShadow = instagramStyle.contentShadow;
        
        calloutElement.textContent = slide.callout;
        slideContainer.appendChild(calloutElement);
      }

      document.body.appendChild(slideContainer);

      // Wait a bit for rendering
      setTimeout(async () => {
        try {
          const canvas = await html2canvas(slideContainer, {
            width: 1024,
            height: 1024,
            scale: 1,
            backgroundColor: null,
            useCORS: true,
            allowTaint: true,
          });
          
          document.body.removeChild(slideContainer);
          resolve(canvas.toDataURL('image/png'));
        } catch (error) {
          console.error('Error rendering slide:', error);
          document.body.removeChild(slideContainer);
          resolve(null);
        }
      }, 100);
    });
  };

  const downloadSlide = async (slideIndex) => {
    if (!slides[slideIndex]) return;
    
    setIsDownloading(true);
    
    try {
      const imageData = await renderSlideAsImage(slides[slideIndex], slideIndex + 1, slides.length);
      
      if (imageData) {
        const link = document.createElement('a');
        link.download = `slide-${slideIndex + 1}.png`;
        link.href = imageData;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      }
    } catch (error) {
      console.error('Error downloading slide:', error);
    } finally {
      setIsDownloading(false);
    }
  };

  const downloadAllSlides = async () => {
    setIsDownloading(true);
    
    try {
      const downloadPromises = slides.map(async (slide, index) => {
        const imageData = await renderSlideAsImage(slide, index + 1, slides.length);
        return { imageData, index };
      });
      
      const results = await Promise.all(downloadPromises);
      
      results.forEach(({ imageData, index }) => {
        if (imageData) {
          setTimeout(() => {
            const link = document.createElement('a');
            link.download = `slide-${index + 1}.png`;
            link.href = imageData;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
          }, index * 500); // Stagger downloads
        }
      });
      
    } catch (error) {
      console.error('Error downloading slides:', error);
    } finally {
      setIsDownloading(false);
    }
  };

  return (
    <div className="slide-generator">
      {/* Full-screen loading overlay */}
      {isGenerating && (
        <div className="slides-generation-overlay">
          <div className="slides-generation-content">
            <div className="slides-generation-animation">
              <div className="pulse-ring"></div>
              <div className="pulse-ring"></div>
              <div className="pulse-ring"></div>
              <div className="slides-generation-icon">📊</div>
            </div>
            <h2>Creating Your Instagram Slides</h2>
            <p>Transforming your tweet into engaging carousel content</p>
            
            <div className="slides-generation-progress">
              <div className="progress-bar-wrapper">
                <div className="progress-bar">
                  <div 
                    className="progress-fill" 
                    style={{ width: `${loadingProgress}%` }}
                  ></div>
                </div>
                <span className="progress-text">{loadingProgress}%</span>
              </div>
              <p className="progress-status">
                {loadingMessage || 'Getting started...'}
              </p>
            </div>
          </div>
        </div>
      )}

      <div className="dashboard-content">
        <div className="content-header">
          <h1 className="page-title">🎨 Slides Generator</h1>
          <p className="page-description">Transform tweets into engaging Instagram carousel slides</p>
        </div>

        {/* Input Section */}
        <div className="section-card">
          <div className="card-header">
            <h3 className="card-title">📝 Tweet to Slides</h3>
            <p className="card-description">
              Paste any Twitter/X URL to create professional Instagram carousel content
            </p>
          </div>
          
          <div className="slide-input-section">
            <div className="input-group">
              <input
                type="text"
                className="form-input"
                placeholder="https://twitter.com/user/status/123... or https://x.com/user/status/123..."
                value={tweetUrl}
                onChange={(e) => setTweetUrl(e.target.value)}
                disabled={isGenerating}
              />
              <button
                onClick={generateSlides}
                disabled={isGenerating || !tweetUrl.trim()}
                className="generate-slides-btn"
              >
                {isGenerating ? (
                  <>
                    <div className="btn-spinner"></div>
                    Generating...
                  </>
                ) : (
                  <>
                    <span className="btn-icon">✨</span>
                    Generate Slides
                  </>
                )}
              </button>
            </div>

            {error && (
              <div className="error-alert">
                <span className="alert-icon">⚠️</span>
                {error}
                {(error.includes('busy') || error.includes('overloaded') || error.includes('rate limit')) && (
                  <button
                    onClick={generateSlides}
                    disabled={isGenerating}
                    className="retry-btn"
                    style={{
                      marginLeft: '10px',
                      padding: '4px 12px',
                      background: '#22c55e',
                      color: '#000000',
                      border: 'none',
                      borderRadius: '6px',
                      fontSize: '0.875rem',
                      cursor: 'pointer',
                      fontWeight: '500'
                    }}
                  >
                    🔄 Try Again
                  </button>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Recent Slides Section */}
        <div className="section-card">
          <div className="card-header">
            <h3 className="card-title">📚 Recent Slides</h3>
            <p className="card-description">Your previously generated slide sets</p>
          </div>
          
          <div className="recent-slides-section">
            {isLoadingRecent ? (
              <div className="loading-recent">
                <div className="loading-spinner"></div>
                <p>Loading recent slides...</p>
              </div>
            ) : recentSlides.length > 0 ? (
              <div className="recent-slides-grid">
                {recentSlides.map((slideSet) => (
                  <div key={slideSet.id} className="recent-slide-card">
                    <div className="slide-preview">
                      {slideSet.imageUrls && slideSet.imageUrls[0] && (
                        <img 
                          src={slideSet.imageUrls[0]} 
                          alt={slideSet.title}
                          className="slide-thumbnail-image"
                        />
                      )}
                      <div className="slide-overlay">
                        <span className="slide-count">{slideSet.slideCount} slides</span>
                      </div>
                    </div>
                    <div className="slide-info">
                      <h4 className="slide-title">{slideSet.title}</h4>
                      <p className="slide-date">{formatTimeAgo(slideSet.createdAt)}</p>
                      <div className="slide-actions">
                        <button
                          onClick={() => loadSavedSlides(slideSet)}
                          className="load-btn"
                        >
                          📝 Load
                        </button>
                        <button
                          onClick={() => deleteSavedSlides(slideSet.id, slideSet.imageUrls)}
                          className="delete-btn"
                        >
                          🗑️ Delete
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="empty-recent">
                <p>No slides generated yet. Create your first slide set above!</p>
              </div>
            )}
          </div>
        </div>

        {/* Slides Preview */}
        {slides.length > 0 && (
          <div className="slides-section">
            <div className="section-card">
              <div className="card-header">
                <h3 className="card-title">📱 Your Slides ({slides.length} slides)</h3>
                <p className="card-description">
                  Instagram carousel ready • Swipe through or use navigation
                </p>
              </div>

              {/* Slide Navigation */}
              <div className="slide-navigation">
                {slides.map((_, index) => (
                  <button
                    key={index}
                    className={`nav-dot ${currentSlide === index ? 'active' : ''}`}
                    onClick={() => setCurrentSlide(index)}
                  >
                    {index + 1}
                  </button>
                ))}
              </div>

              {/* Current Slide Display */}
              <div className="slide-viewer">
                <div className="slide-display-container">
                  {isGeneratingPreviews ? (
                    <div className="slide-generating">
                      <div className="loading-spinner"></div>
                      <p>Generating slide previews...</p>
                    </div>
                  ) : slideImages[currentSlide] ? (
                    <div className="slide-image-display">
                      <img 
                        src={slideImages[currentSlide]} 
                        alt={`Slide ${currentSlide + 1}`}
                        className="slide-preview-image"
                      />
                    </div>
                  ) : (
                    <div className="slide-display">
                      <div className="slide-header">
                        <span className="slide-counter">
                          {currentSlide + 1} / {slides.length}
                        </span>
                      </div>
                      
                      <div className="slide-content-area">
                        <h2 className="slide-title">{slides[currentSlide]?.title}</h2>
                        <p className="slide-text">{slides[currentSlide]?.content}</p>
                        {slides[currentSlide]?.callout && (
                          <div className="slide-callout">
                            {slides[currentSlide].callout}
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>

                {/* Slide Controls */}
                <div className="slide-controls">
                  <button
                    onClick={() => setCurrentSlide(Math.max(0, currentSlide - 1))}
                    disabled={currentSlide === 0}
                    className="control-btn prev-btn"
                  >
                    ← Previous
                  </button>
                  
                  <div className="slide-actions">
                    <button
                      onClick={() => downloadSlide(currentSlide)}
                      disabled={isDownloading}
                      className="action-btn download-single-btn"
                    >
                      {isDownloading ? '⏳ Generating...' : '📸 Download This Slide'}
                    </button>
                    <button
                      onClick={downloadAllSlides}
                      disabled={isDownloading}
                      className="action-btn download-all-btn"
                    >
                      {isDownloading ? '⏳ Generating All...' : '📦 Download All Slides'}
                    </button>
                  </div>

                  <button
                    onClick={() => setCurrentSlide(Math.min(slides.length - 1, currentSlide + 1))}
                    disabled={currentSlide === slides.length - 1}
                    className="control-btn next-btn"
                  >
                    Next →
                  </button>
                </div>
              </div>

              {/* All Slides Grid */}
              <div className="slides-grid-section">
                <h4>All Slides Overview</h4>
                <div className="slides-grid">
                  {slides.map((slide, index) => (
                    <div
                      key={index}
                      className={`slide-thumbnail ${currentSlide === index ? 'active' : ''}`}
                      onClick={() => setCurrentSlide(index)}
                    >
                      <div className="thumbnail-number">#{index + 1}</div>
                      <div className="thumbnail-content">
                        <h5>{slide.title}</h5>
                        <p>{slide.content.substring(0, 100)}...</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default SlideGenerator; 